import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import { IconLink } from '@tabler/icons-react';
import { useAtom } from 'jotai';

import type { AnswerSource } from '@cfa/pathway-fe-shared/xp';
import { Button } from '@cfa/react-core';

import { previewingSourceAtom } from '../state';

import { type ExcerptMatch, findExcerptMatches } from './excerptMatcher';

interface TextSegment {
  text: string;
  match?: ExcerptMatch;
}

/**
 * Splits text into segments based on excerpt matches
 */
const splitTextIntoSegments = (
  text: string,
  matches: ExcerptMatch[],
): TextSegment[] => {
  if (matches.length === 0) {
    return [{ text }];
  }

  const segments: TextSegment[] = [];
  let currentIndex = 0;

  for (const match of matches) {
    // Add text before the match
    if (match.startIndex > currentIndex) {
      segments.push({
        text: text.substring(currentIndex, match.startIndex),
      });
    }

    // Add the matched text with match data
    segments.push({
      text: text.substring(match.startIndex, match.endIndex),
      match,
    });

    currentIndex = match.endIndex;
  }

  // Add remaining text after the last match
  if (currentIndex < text.length) {
    segments.push({
      text: text.substring(currentIndex),
    });
  }

  return segments;
};

interface HighlightedExcerptProps {
  text: string;
  source: AnswerSource;
}

/**
 * Renders a matched text segment with a dashed underline to indicate it's sourced
 */
const HighlightedExcerpt: React.FC<HighlightedExcerptProps> = ({
  text,
  source,
}) => {
  const [previewingSource, setPreviewingSource] = useAtom(previewingSourceAtom);
  const previewing = previewingSource?.text === text;

  const trimmed = text.trimEnd().replace(/\s+([.,!?;:])$/, '$1');

  return (
    <span className="inline">
      {/* PATHWAY-3059 removing the highlight when previewing the source */}
      {/* <span className={cn({ 'bg-brand-navy/15': previewing })}>{trimmed}</span> */}
      <span>{trimmed}</span>
      <Button
        className="bg-brand-navy/50! hover:bg-brand-navy/80! cursor-cursor mx-1! inline-flex h-4! min-h-0! w-6! border-0! p-0! align-baseline transition-colors"
        onPress={() =>
          setPreviewingSource(previewing ? undefined : { source, text })
        }
      >
        <IconLink size={10} className="cursor-pointer" />
      </Button>
    </span>
  );
};

interface TextWithExcerptsProps {
  text: string;
  sources: AnswerSource[];
}

/**
 * Renders text with dashed underlines for any portions that match source excerpts
 */
const TextWithExcerpts: React.FC<TextWithExcerptsProps> = ({
  text,
  sources,
}) => {
  const matches = findExcerptMatches(text, sources);
  const segments = splitTextIntoSegments(text, matches);

  return (
    <>
      {segments.map((segment, index) => {
        const match = segment.match;

        if (match) {
          const source = sources.find(s => s.id === match.sourceId);
          if (source) {
            return (
              <HighlightedExcerpt
                key={`${match.startIndex}-${index}`}
                text={segment.text}
                source={source}
              />
            );
          }
        }

        const prevSegment = segments[index - 1];
        const displayText = prevSegment?.match
          ? segment.text.trimStart()
          : segment.text;

        return <span key={`text-${index}`}>{displayText}</span>;
      })}
    </>
  );
};

/**
 * Recursively processes React children to highlight text that matches source excerpts
 */
const processChildren = (
  children: ReactNode,
  sources: AnswerSource[],
): ReactNode =>
  Children.map(children, child => {
    // If it's a string, process it for highlighting
    if (typeof child === 'string') {
      return <TextWithExcerpts text={child} sources={sources} />;
    }

    // If it's a valid React element with children, recursively process
    if (isValidElement(child)) {
      const element = child as ReactElement<{ children?: ReactNode }>;
      if (element.props.children) {
        return cloneElement(element, {
          children: processChildren(element.props.children, sources),
        });
      }
    }

    // Return as-is for other types
    return child;
  });

interface ExcerptHighlighterProps {
  children: ReactNode;
  sources: AnswerSource[];
}

/**
 * Component that highlights text matching source excerpts within its children.
 * Wraps content and recursively processes text nodes to add dashed underlines
 * for text that matches source excerpts.
 */
export const ExcerptHighlighter: React.FC<ExcerptHighlighterProps> = ({
  children,
  sources,
}) => <>{processChildren(children, sources)}</>;
